import { Box, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useTranslate } from "react-admin";
import { PieChart } from "@mui/x-charts/PieChart";
import { HistoryVoucherType } from "@/types";
import { getVoucherTypeLabel } from "@/constants/voucherType";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";

export const StatTile = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <Paper
    elevation={1}
    sx={{
      p: 2.5,
      borderRadius: 3,
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: "action.hover",
        display: "inline-flex",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} lineHeight={1.1}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

type StatsHistoryVoucherProps = {
  history: HistoryVoucherType[] | [];
};
type VoucherType = "discount" | "gift_card" | "bogo" | "free_product";
type Counts = Record<VoucherType, number>;

const StatsHistoryVoucher = ({ history }: StatsHistoryVoucherProps) => {
  const init: Counts = { discount: 0, gift_card: 0, bogo: 0, free_product: 0 };
  const translate = useTranslate();
  const theme = useTheme();
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    "#4339F2",
    "#F2C239",
  ];
  const settings = {
    margin: { right: 5 },
    width: 400,
    height: 300,
  };

  history.reduce((acc, { voucher_type, type }) => {
    if (voucher_type in acc)
      if (type === "consumed") acc[voucher_type as VoucherType] += 1;
    return acc;
  }, init);

  return (
    <>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 12 }}>
          <Typography variant="h6" gutterBottom>
            {translate("ra.gameoo.dashboard.voucherConsumedTitle")}
          </Typography>
          <PieChart
            series={[
              {
                data: [
                  {
                    id: 0,
                    value: init.discount,
                    label: getVoucherTypeLabel("discount"),
                    color: COLORS[1],
                  },
                  {
                    id: 1,
                    value: init.gift_card,
                    label: getVoucherTypeLabel("gift_card"),
                    color: COLORS[0],
                  },
                  {
                    id: 2,
                    value: init.bogo,
                    label: getVoucherTypeLabel("bogo"),
                    color: COLORS[2],
                  },
                  {
                    id: 3,
                    value: init.free_product,
                    label: getVoucherTypeLabel("free_product"),
                    color: COLORS[3],
                  },
                ],
                highlightScope: { fade: "global", highlight: "item" },
                faded: {
                  innerRadius: 60,
                  additionalRadius: -60,
                  color: "gray",
                },
                innerRadius: 40,
                arcLabel: "value",
              },
            ]}
            {...settings}
          />
        </Grid>
      </Grid>{" "}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <StatTile
            icon={<LocalActivityIcon />}
            label={getVoucherTypeLabel("discount")}
            value={new Intl.NumberFormat("fr-FR").format(init.discount)}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <StatTile
            icon={<LocalActivityIcon />}
            label={getVoucherTypeLabel("gift_card")}
            value={new Intl.NumberFormat("fr-FR").format(init.gift_card)}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <StatTile
            icon={<LocalActivityIcon />}
            label={getVoucherTypeLabel("bogo")}
            value={new Intl.NumberFormat("fr-FR").format(init.bogo)}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <StatTile
            icon={<LocalActivityIcon />}
            label={getVoucherTypeLabel("free_product")}
            value={new Intl.NumberFormat("fr-FR").format(init.free_product)}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default StatsHistoryVoucher;
